import { TermSearchSort } from '@dealpoint/api-models'
import {
  DocuGistKeyTermSearchValue,
  DocuGistQuantityAggregation,
  IPagedDataResults,
} from '@dealpoint/deprecated-lib/src/Models'
import {
  DocumentSearchSortBy,
  IContractSearchQuery,
  IKeyTerm,
  ITermAnalyticsSearchResult,
  IUser,
  RolePermissionsValue,
  SortOrder,
} from '@dealpoint/types'
import { Core } from '@pdftron/webviewer'
import { PayloadAction } from '@reduxjs/toolkit'

export type PropsWithLoading<P> = P & { loading: boolean }

export interface IPropsWithClassName {
  className?: string
}
export type PropsWithClassName<P> = P & IPropsWithClassName

export type OverflowCss = 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto' | 'inherit' | 'unset'

declare global {
  interface Window {
    pendo: { initialize: (options: any) => void }
  }
}

export interface ITermSearchFilter {
  id: string
  searchTerm?: DocuGistKeyTermSearchValue
  disabled?: boolean
}

export interface ISearchQuery {
  active?: boolean
  contractTypes?: string[]
  createdFrom?: number
  createdTo?: number
  endFrom?: number
  endTo?: number
  endTimeUnset?: boolean
  expired?: boolean
  filters?: ITermSearchFilter[]
  limit?: number
  matchedTermTypes?: string[]
  query?: string
  signedAfter?: number
  signedBefore?: number
  sortBy?: DocumentSearchSortBy
  sortOrder?: SortOrder
  startFrom?: number
  startTo?: number
}

export interface ISearchFilter extends Partial<ISearchQuery> {
  id: string
}

export interface IDeprecatedSearchQuery {
  filters?: ITermSearchFilter[]
  query?: string
  signedAfter?: Date
  signedBefore?: Date
}

export interface ITermTypeSearchQuery {
  contractTypeId?: string
  page?: number
  pageSize?: number
  query?: string
  sort?: TermSearchSort
}

export type PartialAtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

export interface IUiUser extends IUser {
  isAdmin?: boolean
  isSelected?: boolean
  phoneVerified?: boolean
}

export type BulmaSize = 'small' | 'normal' | 'medium' | 'large'

const bulmaButtonColors = [
  'black',
  'danger',
  'dark',
  'ghost',
  'info',
  'light',
  'link',
  'primary',
  'success',
  'text',
  'warning',
  'white',
] as const

export type BulmaButtonColor = (typeof bulmaButtonColors)[number]

export type SearchId = string

export interface IDocumentSearchResponse<T = any> {
  description?: string
  query?: string
  results?: IPagedDataResults<T>
}

export interface ISearch<T> extends IDocumentSearchResponse<T> {
  id: SearchId
  error?: boolean
}

export type Aggregate = 'avg' | 'max' | 'min' | 'sum'

export type SearchAggregates = Record<SearchId, Aggregate>

export type AggregateResultsView = 'horizontal' | 'vertical'

export interface IAggregateSearch extends ISearch<DocuGistQuantityAggregation> {
  aggregate: Aggregate
  filteredRecords?: DocuGistQuantityAggregation[]
  originalTotalRecords: number
  searchQuery?: IContractSearchQuery
  status?: Status
  submittedSearchQuery?: IContractSearchQuery
  view: AggregateResultsView
}

export interface ITermAnalyticsSearch extends ISearch<ITermAnalyticsSearchResult> {
  loading?: boolean
  searchQuery?: IContractSearchQuery
  submittedSearchQuery?: IContractSearchQuery
}

export type PartialTermSearchValue = PartialAtLeast<DocuGistKeyTermSearchValue, 'termTypeID'>

export interface IPdfSelectedText {
  highlightColor: string
  pageNumber: number
  quads: Core.Math.Quad[]
  text: string
}

export const apiMethods = ['DELETE', 'GET', 'PATCH', 'POST', 'PUT'] as const
export type ApiMethod = (typeof apiMethods)[number]

export const status = [
  'init',
  'idle',
  'loading',
  'error',
  'saving',
  'saved',
  'deleting',
  'deleted',
] as const
export type Status = (typeof status)[number]

export interface IQuantityUnit {
  title: string
}

export type DocumentKeyTermType = 'attributeKeyTerms' | 'documentKeyTerms'

export type VariantSize = 'small' | 'medium' | 'large'

export type VariantColor = 'default' | 'primary' | 'secondary' | 'transparent' | 'danger'

export interface IFriendlyErrorMessage {
  title?: string
  description: string
}

export const CAN_MANAGE_USERS = 'CAN_MANAGE_USERS'
export const CAN_MANAGE_SUBSCRIPTION = 'CAN_MANAGE_SUBSCRIPTION'
export const CAN_MANAGE_ORG = 'CAN_MANAGE_ORG'
export const permissions = [CAN_MANAGE_ORG, CAN_MANAGE_SUBSCRIPTION, CAN_MANAGE_USERS] as const
export type AdminPermission = (typeof permissions)[number]

export interface ISubscription {
  documents: number
  id: string
  name: string
  users: number
}

export type PayloadActionWithId<T> = PayloadAction<T & { id: string }>

export type ExpirationStatus = 'active' | 'expired' | 'unknown' | 'invalid' | 'notStarted'

const pdfTronWebViewerElements = [
  'leftPanelButton',
  'viewControlsButton',
  'panToolButton',
  'selectToolButton',
  'fitButton',
  'zoomOutButton',
  'zoomInButton',
  'freeHandToolGroupButton',
  'freeHandToolButton',
  'freeHandToolButton2',
  'freeHandToolButton3',
  'freeHandToolButton4',
  'textToolGroupButton',
  'highlightToolButton',
  'highlightToolButton2',
  'highlightToolButton3',
  'highlightToolButton4',
  'underlineToolButton',
  'squigglyToolButton',
  'strikeoutToolButton',
  'shapeToolGroupButton',
  'rectangleToolButton',
  'ellipseToolButton',
  'lineToolButton',
  'arrowToolButton',
  'polylineToolButton',
  'polygonToolButton',
  'cloudToolButton',
  'eraserToolButton',
  'signatureToolGroupButton',
  'freeTextToolButton',
  'stickyToolButton',
  'miscToolGroupButton',
  'calloutToolButton',
  'stampToolGroupButton',
  'rubberStampToolGroupButton',
  'fileAttachmentToolGroupButton',
  'cropToolButton',
  'toolsButton',
  'searchButton',
  'menuButton',
  'leftPanel',
  'notesPanelButton',
  'toggleNotesButton',
  'notesPanel',
  'thumbnailsPanelButton',
  'thumbnailsPanel',
  'thumbnailControl',
  'documentControl',
  'outlinesPanelButton',
  'outlinesPanel',
  'layersPanelButton',
  'layersPanel',
  'bookmarksPanelButton',
  'bookmarksPanel',
  'searchPanel',
  'viewControlsOverlay',
  'defaultPageTransitionButton',
  'continuousPageTransitionButton',
  'layoutHeader',
  'singleLayoutButton',
  'doubleLayoutButton',
  'coverLayoutButton',
  'rotateCounterClockwiseButton',
  'rotateClockwiseButton',
  'menuOverlay',
  'filePickerButton',
  'fullscreenButton',
  'downloadButton',
  'printButton',
  'pageNavOverlay',
  'toolsOverlay',
  'searchOverlay',
  'annotationPopup',
  'annotationCommentButton',
  'annotationStyleEditButton',
  'annotationDeleteButton',
  'annotationCropButton',
  'annotationRedactButton',
  'annotationGroupButton',
  'annotationUngroupButton',
  'calibrateButton',
  'linkButton',
  'fileAttachmentDownload',
  'annotationStylePopup',
  'toolStylePopup',
  'stylePopup',
  'textPopup',
  'copyTextButton',
  'textHighlightToolButton',
  'textUnderlineToolButton',
  'textSquigglyToolButton',
  'textStrikeoutToolButton',
  'contextMenuPopup',
  'panToolButton',
  'stickyToolButton',
  'highlightToolButton',
  'freeHandToolButton',
  'freeTextToolButton',
  'annotationNoteConnectorLine',
  'signatureModal',
  'signatureModalCloseButton',
  'signatureModalClearButton',
  'signatureModalSignButton',
  'inkSignaturePanelButton',
  'textSignaturePanelButton',
  'imageSignaturePanelButton',
  'printModal',
  'loadingModal',
  'errorModal',
  'passwordModal',
  'progressModal',
  'themeChangeButton',
  'ribbons',
  'toolsHeader',
] as const
export type PdfTronWebViewerElements = (typeof pdfTronWebViewerElements)[number]

export type DocumentId = string

export type KeyTermType = 'attribute' | 'document'

export type DataPrivacySource = 'instagram'

export type EnrichedKeyTermError =
  | 'termNotInGroup'
  | 'noTermSelected'
  | 'noHighlight'
  | 'wrongVersion'

export interface IEnrichedKeyTerm extends IKeyTerm {
  errors?: EnrichedKeyTermError[]
  title: string
}

export interface IResourcePermissions {
  id: string
  permissions: RolePermissionsValue[]
}

export type ChangeType = 'add' | 'remove' | 'update'
